// CSV parsing helpers — wraps PapaParse so the module surface stays small.
// PapaParse is bundled by esbuild via the import below (npm dep, ^5.5.3).

import Papa from 'papaparse';

// Hard cap on raw file size before parsing — matches the spec's "no surprise"
// budget. The 500-row RPC cap downstream is the authoritative limit; this
// guard just stops us from feeding multi-megabyte files into the parser.
export const MAX_FILE_BYTES = 1024 * 1024; // 1 MB

// Decode raw file bytes into a JS string, choosing an encoding instead of
// letting FileReader/Papa assume UTF-8. Excel on Israeli Windows exports
// Hebrew CSVs as Windows-1255 (single-byte): under a UTF-8 read the Hebrew
// bytes (0xE0-0xFA) are invalid sequences → U+FFFD ("?????") while ASCII
// (Latin, emails, ISO dates) survives. Strategy:
//   1. BOM sniff — UTF-8 BOM (strip it; also fixes a stray ﻿ leaking
//      onto the first header), UTF-16 LE/BE.
//   2. No BOM → strict UTF-8 ({fatal:true} THROWS on the first invalid
//      sequence — a deterministic signal, unlike post-hoc U+FFFD sniffing).
//   3. On throw → Windows-1255 (natively supported by TextDecoder; no dep).
// A truly binary file that's neither valid UTF-8 nor meaningful 1255 still
// decodes to junk here, but flow.js's BAD_HEADER_CHAR guard rejects it.
function _decodeCsvBytes(buf) {
  if (buf.length >= 3 && buf[0] === 0xEF && buf[1] === 0xBB && buf[2] === 0xBF) {
    return new TextDecoder('utf-8').decode(buf.subarray(3));
  }
  if (buf.length >= 2 && buf[0] === 0xFF && buf[1] === 0xFE) {
    return new TextDecoder('utf-16le').decode(buf.subarray(2));
  }
  if (buf.length >= 2 && buf[0] === 0xFE && buf[1] === 0xFF) {
    return new TextDecoder('utf-16be').decode(buf.subarray(2));
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1255').decode(buf);
  }
}

// Parse a File (from <input type=file>) and return:
//   { headers: [string], rows: [{ [header]: cell }], totalRows: number,
//     errors: [PapaParseError], meta: { delimiter, linebreak, ... } }
// Throws on file-size violation or unrecoverable parse failure. Per-row
// parse warnings come back in the `errors` array (best-effort, mirrors
// the bulk_create_specialist_patients RPC contract).
//
// We read the bytes ourselves and decode (see _decodeCsvBytes) before
// handing a STRING to Papa — Papa parses a string synchronously, so the
// results-handling below is unchanged from the File-input version.
export async function parseCsvFile(file) {
  if (!file) throw new Error('No file provided');
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('File too large (max ' + Math.round(MAX_FILE_BYTES / 1024) + 'KB)');
  }
  const buf = new Uint8Array(await file.arrayBuffer());
  const text = _decodeCsvBytes(buf);
  return new Promise((resolve, reject) => {
    Papa.parse(text, {
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: (h) => (h || '').trim(),
      complete: (results) => {
        const headers = results.meta?.fields || [];
        const rows = results.data || [];
        resolve({
          headers,
          rows,
          totalRows: rows.length,
          errors: results.errors || [],
          meta: results.meta || {},
        });
      },
      error: (err) => reject(err),
    });
  });
}
